/* eslint-disable */
import { memo, useEffect, useState } from 'react';
import MinMaxInput from '@/Components/AddProductParameters/minMaxInput';
import { Button } from '@/Components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { urls } from '@/Constants';
import { Tooltip } from '@mui/material';
import { Trash2, Check, RefreshCcw, Settings2, FilePenLine, X, TriangleAlert } from 'lucide-react';
import moment from 'moment';
import { SelectPicker, Whisper, Popover } from 'rsuite';
import { getLCM, isNotMultipleOf } from '../utils/utils';

const toDisplayQty = (v) => (v ? String(v) : '0');

const ProductionQtyInput = memo(function ProductionQtyInput({
    value,
    unit,
    rowIndex,
    header,
    disabled,
    className,
    placeholder,
    handleDateQtyChange,
    handleBlur,
}) {
    const [localValue, setLocalValue] = useState(toDisplayQty(value));

    useEffect(() => {
        setLocalValue(toDisplayQty(value));
    }, [value]);

    const blockDecimal = ['pc', 'count'].includes(unit);

    const handleLocalChange = (e) => {
        const v = e.target.value;
        if (blockDecimal && v.toString().includes('.')) return;
        setLocalValue(v);
    };

    const commit = () => {
        handleDateQtyChange(localValue, rowIndex, header);
    };

    return (
        <input
            type='number'
            className={className}
            value={localValue}
            disabled={disabled}
            onChange={handleLocalChange}
            onBlur={commit}
            onKeyDown={(e) =>
                ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()
            }
            placeholder={placeholder}
        />
    );
});

const renderPackSize = (rowData, rowIndex) => {
    const packSize = rowData?.packSize;
    if (!packSize || rowData?.originalUnit !== 'count') return null;
    return (
        <div className='flex items-center justify-center text-[12px] font-medium'>
            {rowData._isUnpacked ? (
                `Unpacked (1)`
            ) : (
                `Pack Of ${packSize.quantity}${packSize.boxName ? ` (${packSize.boxName})` : ''}`
            )}
        </div>
    );
};

const longTermProductionStepRender = ({ rowData, rowIndex, header, context }) => {
    const { handleDeleteRow, handleChange, handleBlur, viewMode, state } = context;
    switch (header.id) {
        case 'S.No':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowIndex + 1}
                </div>
            );

        case 'productName':
            return (
                <div className='flex items-center w-full justify-start text-left text-[12px] px-2 whitespace-normal break-words leading-tight py-1'>
                    {rowData.productName || '-'}
                </div>
            );

        case 'clientVariantId':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.clientVariantId || '-'}
                </div>
            );
        case 'packSize':
            return renderPackSize(rowData, rowIndex);
        case 'batch':
            if (!rowData?.batch) {
                return <Button onClick={() => window.open(`${urls.EXTENSION}${urls.VARIANT_BATCH_SIZE}?productId=${encodeURIComponent(rowData._productId)}&variantId=${encodeURIComponent(rowData._variantId)}&storeId=${encodeURIComponent(state?.kitchen)}&productName=${encodeURIComponent(rowData.productName)}`)} className='px-2 h-6 text-[11px] bg-blue-400 hover:bg-blue-500 text-white' > Add Batch Size</Button>
            }
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.batch || '-'} {rowData?.batch && `${rowData?.unit}`}
                </div>
            );

        case 'quantity':
            const batchVal = parseFloat(rowData?.batch);
            // Pack size is ignored (treated as none): validity is batch-multiple
            // only, so a value like 11.5 with batch 11.5 must NOT error.
            const packSizeVal = 0;
            const qtyVal = parseFloat(rowData.qty);
            const isInvalidBatchMultiple = isNotMultipleOf(qtyVal, batchVal);
            const isInvalidPackSizeMultiple = isNotMultipleOf(qtyVal, packSizeVal);
            const isTouched = rowData[`_touched_${header.id}`];
            const isBatchMissing = !rowData.batch || batchVal === 0;
            const lcm = batchVal > 0 && packSizeVal > 0 ? getLCM(batchVal, packSizeVal) : 0;
            const suggestedQty = lcm && qtyVal > 0 ? Math.ceil(qtyVal / lcm) * lcm : qtyVal;
            const remainder = packSizeVal > 0 && qtyVal > 0 ? qtyVal % packSizeVal : 0;
            return (
                <div className='flex flex-col items-center justify-center gap-1'>
                    <input
                        type='number'
                        className={`text-center w-[100px] border focus:border-blue-500 outline-none transition-colors duration-200 rounded px-2 py-1 text-[12px] ${(isInvalidBatchMultiple || isInvalidPackSizeMultiple) && isTouched ? 'border-red-500' : 'border-gray-300'
                            } ${isBatchMissing ? 'bg-gray-100 cursor-not-allowed border-gray-200' : ''}`}
                        value={rowData.qty ?? ''}
                        disabled={viewMode || isBatchMissing}
                        onChange={(e) => handleChange(e.target.value, null, rowData)}
                        onBlur={() => handleBlur(rowIndex, header.id)}
                        onKeyDown={(e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()}
                        placeholder={isBatchMissing ? 'N/A' : 'Enter qty'}
                    />
                    {isBatchMissing ? (
                        <span className='text-red-500 text-[10px] whitespace-nowrap font-medium'>
                            Batch size not defined
                        </span>
                    ) : isTouched ? (isInvalidBatchMultiple || isInvalidPackSizeMultiple) ? (
                        <div className='flex flex-col gap-0.5 items-center leading-tight'>
                            {isInvalidBatchMultiple && isInvalidPackSizeMultiple ? (
                                <span className='text-red-500 text-[10px] text-center'>
                                    Multiple of Batch Size{' '}
                                    {rowData?.batch}{' '}
                                    {rowData?.unit || ''} & Pack Size{' '}
                                    {rowData?.packSize?.quantity}
                                </span>
                            ) : (
                                <>
                                    {isInvalidBatchMultiple && (
                                        <span className='text-red-500 text-[10px]'>
                                            Multiple of Batch Size{' '}
                                            {rowData?.batch}{' '}
                                            {rowData?.unit || ''}
                                        </span>
                                    )}
                                    {isInvalidPackSizeMultiple && (
                                        <span className='text-red-500 text-[10px]'>
                                            Multiple of Pack Size{' '}
                                            {rowData?.packSize?.quantity}
                                        </span>
                                    )}
                                </>
                            )}
                            <div className='flex flex-wrap justify-center gap-x-2'>
                                <span className='text-blue-600 text-[10px] font-bold'>
                                    Suggested: {suggestedQty}
                                </span>
                                {remainder > 0 && (
                                    <span className='text-orange-600 text-[10px] font-bold'>
                                        Leftover: {remainder.toFixed(3)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : null : null}
                </div>
            );

        case 'remove':
            return (
                <div className='flex items-center justify-center'>
                    <Trash2
                        className='w-4 h-4 cursor-pointer text-red-500 hover:text-red-700 transition-colors'
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRow(rowIndex);
                        }}
                        title='Remove row'
                    />
                </div>
            );

        default:
            // Return null to fallback to default renderer
            return (
                <div className='flex items-center justify-center text-[12px] px-2'>
                    {rowData[header.id] ?? '-'}
                </div>
            );
    }
};

const longTermProductionSecondStepRender = ({ rowData, rowIndex, header, context }) => {
    const { handleDeleteRow, handleChange, handleBlur, viewMode, state } = context;
    switch (header.id) {
        case 'S.No':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowIndex + 1}
                </div>
            );

        case 'productName':
            return (
                <div className='flex items-center w-full justify-start text-left text-[12px] px-2 whitespace-normal break-words leading-tight py-1'>
                    {rowData.productName || '-'}
                </div>
            );

        case 'clientVariantId':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.clientVariantId || '-'}
                </div>
            );
        case 'packSize':
            return renderPackSize(rowData);

        case 'batch':
            if (!rowData?.batch) {
                return <Button onClick={() => window.open(`${urls.EXTENSION}${urls.VARIANT_BATCH_SIZE}?productId=${rowData._productId}&variantId=${rowData._variantId}&storeId=${state?.kitchen}&productName=${rowData.productName}`)} className='px-2 h-6 text-[11px] bg-blue-400 hover:bg-blue-500 text-white' > Add Batch Size</Button>
            }
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.batch || '-'} {rowData?.batch && `${rowData?.unit}`}
                </div>
            );

        case 'quantity':
            const batchVal = parseFloat(rowData?.batch);
            // Pack size is ignored (treated as none): validity is batch-multiple only.
            const packSizeVal = 0;
            const qtyVal = parseFloat(rowData.qty);
            const isInvalidBatchMultiple = isNotMultipleOf(qtyVal, batchVal);
            const isInvalidPackSizeMultiple = isNotMultipleOf(qtyVal, packSizeVal);
            const isTouched = rowData[`_touched_${header.id}`];
            const isBatchMissing = !rowData.batch || batchVal === 0;
            const lcm = batchVal > 0 && packSizeVal > 0 ? getLCM(batchVal, packSizeVal) : (batchVal > 0 ? batchVal : (packSizeVal > 0 ? packSizeVal : 0));
            const suggestedQty = lcm && qtyVal > 0 ? Math.ceil(qtyVal / lcm) * lcm : qtyVal;
            const remainder = packSizeVal > 0 && qtyVal > 0 ? qtyVal % packSizeVal : 0;

            return (
                <div className='flex flex-col items-center justify-center gap-1'>
                    <input
                        type='number'
                        className={`text-center w-[100px] border focus:border-blue-500 outline-none transition-colors duration-200 rounded px-2 py-1 text-[12px] ${(isInvalidBatchMultiple || isInvalidPackSizeMultiple) && isTouched ? 'border-red-500' : 'border-gray-300'
                            } ${isBatchMissing ? 'bg-gray-100 cursor-not-allowed border-gray-200' : ''}`}
                        value={rowData.qty ?? ''}
                        disabled={viewMode || isBatchMissing}
                        onChange={(e) => handleChange(e.target.value, null, rowData)}
                        onBlur={() => handleBlur(rowIndex, header.id)}
                        onKeyDown={(e) => ['-', '+', "'", 'e', 'E'].includes(e.key) && e.preventDefault()}
                        placeholder={isBatchMissing ? 'N/A' : 'Enter qty'}
                    />
                    {isBatchMissing ? (
                        <span className='text-red-500 text-[10px] whitespace-nowrap font-medium'>
                            Batch size not defined
                        </span>
                    ) : isTouched && (isInvalidBatchMultiple || isInvalidPackSizeMultiple) ? (
                        <div className='flex flex-col gap-0.5 items-center leading-tight'>
                            {isInvalidBatchMultiple && isInvalidPackSizeMultiple ? (
                                <span className='text-red-500 text-[10px] text-center'>
                                    Multiple of Batch Size{' '}
                                    {rowData?.batch}{' '}
                                    {rowData?.unit || ''} & Pack Size{' '}
                                    {rowData?.packSize?.quantity}
                                </span>
                            ) : (
                                <>
                                    {isInvalidBatchMultiple && (
                                        <span className='text-red-500 text-[10px]'>
                                            Multiple of Batch Size{' '}
                                            {rowData?.batch}{' '}
                                            {rowData?.unit || ''}
                                        </span>
                                    )}
                                    {isInvalidPackSizeMultiple && (
                                        <span className='text-red-500 text-[10px]'>
                                            Multiple of Pack Size{' '}
                                            {rowData?.packSize?.quantity}
                                        </span>
                                    )}
                                </>
                            )}
                            <div className='flex flex-wrap justify-center gap-x-2'>
                                <span className='text-blue-600 text-[10px] font-bold'>
                                    Suggested: {suggestedQty}
                                </span>
                                {remainder > 0 && (
                                    <span className='text-orange-600 text-[10px] font-bold'>
                                        Leftover: {remainder.toFixed(3)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            );

        case 'remove':
            return (
                <div className='flex items-center justify-center'>
                    <Trash2
                        className='w-4 h-4 cursor-pointer text-red-500 hover:text-red-700 transition-colors'
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRow(rowIndex);
                        }}
                        title='Remove row'
                    />
                </div>
            );

        default:
            // Return null to fallback to default renderer
            return (
                <div className='flex items-center justify-center text-[12px] px-2'>
                    {rowData[header.id] ?? '-'}
                </div>
            );
    }
};

const longTermProductionThirdStepRender = ({ rowData, rowIndex, header, context }) => {
    const { handleCheckBoxChange, handleFrequencyChange, handleRepeatDaysChange, viewMode, handleCustomDates, workerAllocations } = context;
    if (String(header.id).startsWith('date_')) {
        const isScheduled = rowData[header.id];
        const dateStr = header.id.replace('date_', '');
        const isPastDate = moment(dateStr).isBefore(moment(), 'day');
        const allocation = workerAllocations?.[dateStr];
        const isHoliday = allocation?.isHoliday !== undefined && allocation.isHoliday;
        const isDisabled = isPastDate || isHoliday;

        return (
            <div className='flex items-center justify-center w-full h-full'>
                <div
                    className={`w-5 h-5 flex items-center justify-center rounded border transition-colors duration-200 ${isDisabled
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                        : isScheduled
                            ? 'bg-green-600 border-green-600 shadow-sm cursor-pointer'
                            : 'bg-white border-gray-800 hover:border-blue-400 cursor-pointer'
                        }`}
                    onClick={() => !viewMode && !isDisabled && handleCheckBoxChange(!isScheduled, rowIndex, header)}
                >
                    {isDisabled ? (
                        <X className='w-3.5 h-3.5 text-gray-400 stroke-[3]' />
                    ) : (
                        isScheduled && <Check className='w-3.5 h-3.5 text-white stroke-[3]' />
                    )}
                </div>
            </div>
        );
    }
    switch (header.id) {
        case 'frequency':
            const frequencyOptions = [
                { label: 'Daily', value: 'daily' },
                { label: 'Repeat After', value: 'repeat' },
                { label: 'Custom', value: 'custom' },
            ];
            const hasSelectedDate = Object.keys(rowData).some(key => String(key).startsWith('date_') && rowData[key] === true);
            return (
                <div className='flex flex-col gap-2 items-center justify-center px-1 w-full'>
                    {!hasSelectedDate && rowData.frequency === 'repeat' && (
                        <div
                            className='text-blue-500 text-[10px] font-semibold mt-1 text-center'
                            style={{
                                animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                            }}
                        >
                            Please select a starting <br /> date to repeat
                        </div>
                    )}
                    <SelectPicker
                        data={frequencyOptions}
                        value={rowData?.frequency}
                        size='sm'
                        cleanable={false}
                        searchable={false}
                        menuStyle={{
                            zIndex: 4000,
                        }}
                        className='w-full min-w-[100px] text-[12px] [&_.rs-picker-toggle]:!h-8'
                        disabled={viewMode}
                        onChange={(value) => handleFrequencyChange(value, rowIndex, header)}
                    />
                    {rowData.frequency === 'repeat' && (
                        <MinMaxInput
                            id={rowIndex}
                            value={rowData.repeatDaysNumber || ''}
                            handleMinMaxChange={handleRepeatDaysChange}
                            notNegative={true}
                            disabled={viewMode}
                            name='repeatDaysNumber'
                            size='sm'
                        />
                    )}
                    {rowData?.frequency === 'custom' && (
                        <div className='flex items-center gap-1 mt-2'>
                            <button
                                onClick={() => handleCustomDates(rowIndex, true)}
                                className='px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors shadow-sm'
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => handleCustomDates(rowIndex, false)}
                                className='px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors shadow-sm'
                            >
                                DeSelect All
                            </button>
                        </div>
                    )}
                </div>
            );

        case 'S.No':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowIndex + 1}
                </div>
            );

        case 'productName':
            return (
                <div className='flex items-center w-full justify-start text-left text-[12px] px-2 whitespace-normal break-words leading-tight py-1'>
                    {rowData.productName || '-'}{`${rowData?.clientVariantId}`}
                </div>
            );
        case 'packSize':
            return renderPackSize(rowData);

        default:
            // Return null to fallback to default renderer
            return (
                <div className='flex items-center justify-center text-[12px] px-2'>
                    {rowData[header.id] ?? '-'}
                </div>
            );
    }
};

const longTermProductionFourthStepRender = ({ rowData, rowIndex, header, context }) => {
    const { handleDateQtyChange, handleBlur, viewMode, workerAllocations, dateMeta } = context;
    if (String(header.id).startsWith('date_')) {
        const { editMode } = context;
        const dateStr = header.id.replace('date_', '');
        // Read precomputed flags from context instead of running moment() per cell.
        const { isPastDate = false, hasGeneratedPlan = false } =
            dateMeta?.[dateStr] || {};
        const isHoliday = workerAllocations[dateStr]?.isHoliday;
        if (editMode && (isPastDate || hasGeneratedPlan)) {
            const plannedQty = rowData[header.id] === false ? 0 : parseFloat(rowData[header.id]) || 0;
            const fulfilledQty = parseFloat(rowData[`_fulfilled_${header.id}`]) || 0;

            let bgColor = 'bg-slate-200';
            let textColor = 'text-slate-700';

            if (fulfilledQty < plannedQty) {
                bgColor = 'bg-red-200';
                textColor = 'text-red-700';
            } else if (fulfilledQty > plannedQty) {
                bgColor = 'bg-green-200';
                textColor = 'text-green-700';
            }

            return (
                <div className={`flex flex-col items-center justify-center w-[80px] h-[40px] rounded-md ${bgColor} ${textColor} text-[11px] font-medium transition-colors`}>
                    <div className="font-bold">{fulfilledQty}/</div>
                    <div>{plannedQty}</div>
                </div>
            );
        }

        const batchSize = parseFloat(rowData.batch);
        // Pack size is ignored (treated as none): validity is batch-multiple only.
        const packSize = 0;
        const dayQty = parseFloat(rowData[header.id]);

        const isInvalidBatchMultiple = isNotMultipleOf(dayQty, batchSize);
        const isInvalidPackSizeMultiple = isNotMultipleOf(dayQty, packSize);
        const isInvalidDayQty = isInvalidBatchMultiple || isInvalidPackSizeMultiple;

        const isDayTouched = rowData[`_touched_${header.id}`];
        const isBatchMissing = !rowData.batch || batchSize === 0;
        const shouldShowDash = editMode ? (isPastDate || isHoliday) : rowData[header.id] === false;

        return (
            <div className='flex flex-col items-center justify-center gap-1 w-[80px]'>
                <div className='flex items-center justify-center w-full h-[28px] primary-input-container'>
                    {shouldShowDash ? (
                        <div className='flex items-center justify-center text-[12px] text-gray-400 bg-gray-100 w-full h-full rounded'>
                            -
                        </div>
                    ) : (
                        <ProductionQtyInput
                            key={`${rowIndex}-${header.id}`}
                            value={rowData[header.id]}
                            unit={rowData?.unit}
                            rowIndex={rowIndex}
                            header={header}
                            disabled={viewMode || isBatchMissing}
                            className={`text-center w-full h-full border focus:border-blue-500 outline-none transition-colors duration-200 rounded px-2 text-[12px] ${isInvalidDayQty && isDayTouched ? 'border-red-500' : 'border-gray-300'
                                } ${isBatchMissing ? 'bg-gray-100 cursor-not-allowed border-gray-200' : ''}`}
                            placeholder={isBatchMissing ? 'N/A' : 'Enter qty'}
                            handleDateQtyChange={handleDateQtyChange}
                            handleBlur={handleBlur}
                        />
                    )}
                </div>
                {rowData[header.id] !== false && isBatchMissing && (
                    <span className='text-red-500 text-[8px] leading-[1.1] block text-center mt-1 font-medium w-full break-words'>
                        Batch size<br />not defined
                    </span>
                )}
            </div>
        );
    }
    switch (header.id) {
        case 'S.No':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowIndex + 1}
                </div>
            );

        case 'productName':
            return (
                <div className='flex items-center w-full justify-start text-left text-[12px] px-2 whitespace-normal break-words leading-tight py-1'>
                    {rowData.productName || '-'} {rowData?.clientVariantId}
                </div>
            );

        case 'clientVariantId':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.clientVariantId || '-'}
                </div>
            );
        case 'packSize':
            return renderPackSize(rowData);
        case 'batch':
            return (
                <div className='flex items-center justify-center text-[12px]'>
                    {rowData.batch || '-'} {rowData?.batch && rowData?.unit}
                </div>
            );
        case 'planned':
            return (
                <div className='flex items-center font-bold justify-center text-[12px] w-full'>
                    {rowData.planned || 0} {rowData?.unit}
                </div>
            );
        case 'variance':
            const { handleVarianceAdjust, handleVarianceWriteOff, editMode, workerAllocations, dateMeta: varianceDateMeta } = context;
            const batchSizeForVal = parseFloat(rowData.batch);
            // Pack size is ignored (treated as none): validity is batch-multiple only.
            const packSizeQtyForVal = 0;
            const hasInvalidQty = Object.keys(rowData).some(key => {
                if (String(key).startsWith('date_')) {
                    const dateStr = String(key).replace('date_', '');
                    // Use precomputed flags instead of moment() per date key.
                    const { isPastDate = false, hasGeneratedPlan = false } =
                        varianceDateMeta?.[dateStr] || {};
                    const isHoliday = workerAllocations?.[dateStr]?.isHoliday;

                    if (isPastDate || hasGeneratedPlan || isHoliday) return false;

                    const val = parseFloat(rowData[key]);
                    const isInvalidBatch = isNotMultipleOf(val, batchSizeForVal);
                    const isInvalidPack = isNotMultipleOf(val, packSizeQtyForVal);
                    return isInvalidBatch || isInvalidPack;
                }
                return false;
            });
            const speaker = (
                <Popover
                    className='!p-0 border-none shadow-lg rounded-md overflow-hidden'
                    style={{ zIndex: 9999 }}
                >
                    <div className='flex flex-col p-1 bg-white min-w-[220px]'>
                        {hasInvalidQty && (
                            <div className='px-2 py-2 text-red-500 text-[11px] font-bold border-b border-red-100 bg-red-50 text-center'>
                                Quantities must be multiples of Batch/Pack size
                            </div>
                        )}
                        <div
                            className='flex items-center gap-4 px-2 py-2 hover:bg-gray-50 cursor-pointer transition-colors group'
                            onClick={() => {
                                if (hasInvalidQty) return;
                                handleVarianceAdjust(rowIndex);
                            }}
                        >
                            <Settings2 size={20} className='text-black' />
                            <span className='text-[14px] text-black'>Adjust in remaining days</span>
                        </div>
                        <div
                            className='flex items-center gap-4 px-2 py-2 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors group'
                        // onClick={() => {
                        //     handleVarianceWriteOff(rowIndex);
                        // }}
                        >
                            <FilePenLine size={20} className='text-black' />
                            <span className='text-[14px] text-black'>Write Off</span>
                        </div>
                    </div>
                </Popover>
            );
            return (
                <div className='flex items-center justify-center gap-1 text-[12px] w-full'>
                    {hasInvalidQty && (
                        <Tooltip title='Invalid quantities: production must be multiples of the batch and pack sizes'>
                            <span className='inline-flex items-center text-red-600 cursor-help shrink-0'>
                                <TriangleAlert size={14} className='shrink-0' />
                            </span>
                        </Tooltip>
                    )}
                    <span className={`${(rowData.variance || 0) > 0 ? 'text-green-600' : (rowData.variance || 0) < 0 ? 'text-red-600' : ''}`}>
                        {rowData.variance > 0 ? `+${rowData.variance}` : rowData.variance || 0}
                    </span>
                    {editMode && parseFloat(rowData.variance) !== 0 && (
                        <Whisper
                            placement='bottomStart'
                            trigger='click'
                            speaker={speaker}
                            enterable
                        >
                            <div className='cursor-pointer p-1 hover:bg-gray-100 rounded-full transition-colors'>
                                <RefreshCcw className='text-black' size={16} />
                            </div>
                        </Whisper>
                    )}
                </div>
            );
        case 'finalProduction':
            return (
                <div className='flex items-center font-bold justify-center text-[12px] w-full'>
                    {rowData.finalProduction || 0} {rowData?.unit}
                </div>
            );
        default:
            // Return null to fallback to default renderer
            return (
                <div className='flex items-center justify-center text-[12px] px-2'>
                    {rowData[header.id] ?? '-'}
                </div>
            );
    }
};

export const tableRenderRowData = {
    longTermProduction: {
        1: {
            render: longTermProductionStepRender,
        },
        2: {
            render: longTermProductionSecondStepRender,
        },
        3: {
            render: longTermProductionThirdStepRender,
        },
        4: {
            render: longTermProductionFourthStepRender,
        },
    },
};

export const getTableRenderer = (tableRenderKey, currentStep = 1) => {
    const config = tableRenderRowData[tableRenderKey];
    if (!config) return null;
    if (config[currentStep]?.render) {
        return config[currentStep].render;
    }

    return config.render || null;
};
